import React from 'react';
import { Phone, ArrowLeft, MapPin, Clock, Star } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import VillesVoisines from '../components/VillesVoisines';
import AvisVille from '../components/AvisVille';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../components/ui/accordion';

const ReparationIPhoneRiveDeGier = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour</span>
          </button>
          
          <div className="flex items-center gap-4">
            <a href="tel:0773601521" className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors">
              <Phone className="w-4 h-4" />
              <span className="font-medium">07.73.60.15.21</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#1a2332] via-[#1e2a3d] to-[#1a2332] text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-400 rounded-full px-4 py-2 mb-6">
            <MapPin className="w-4 h-4" />
            <span className="text-sm">À seulement 10 km de Rive-de-Gier</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Réparation iPhone à Rive-de-Gier
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Intervention rapide depuis Saint-Chamond pour Rive-de-Gier et environs - Écran, batterie, connecteur de charge
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="tel:0773601521">
              <Button className="bg-green-500 hover:bg-green-600 text-white px-8 py-6 text-lg rounded-lg font-semibold">
                <Phone className="w-5 h-5 mr-2" />
                Appeler : 07.73.60.15.21
              </Button>
            </a>
            <Link to="/rendez-vous">
              <Button className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg rounded-lg font-semibold">
                Prendre rendez-vous
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Nos services pour Rive-de-Gier
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Réparation d'écran iPhone</h3>
                <p className="text-gray-600 mb-4">
                  Écran cassé, fissuré ou tactile défaillant ? Remplacement avec pièces Premium, Hard OLED ou Soft OLED.
                </p>
                <div className="text-blue-600 font-bold">Devis gratuit</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Remplacement de batterie</h3>
                <p className="text-gray-600 mb-4">
                  Batterie qui se décharge rapidement ? Changement de batterie iPhone en 30 minutes.
                </p>
                <div className="text-blue-600 font-bold">Devis gratuit</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Connecteur de charge</h3>
                <p className="text-gray-600 mb-4">
                  Votre iPhone ne charge plus ? Réparation du port de charge défectueux.
                </p>
                <div className="text-blue-600 font-bold">Sur devis</div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <Link to="/rendez-vous">
              <Button className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg text-lg">
                Demander un devis gratuit
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Pourquoi nous choisir à Rive-de-Gier ?
          </h2>

          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Proximité de Rive-de-Gier</h3>
                <p className="text-gray-600">
                  Notre atelier est situé à Saint-Chamond, à seulement 10 km de Rive-de-Gier. Intervention rapide possible à votre domicile.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Réparation le jour même</h3>
                <p className="text-gray-600">
                  La plupart des réparations (écran, batterie) sont effectuées en 30 minutes à 2 heures. Vous récupérez votre iPhone rapidement.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Star className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Garantie 3 mois</h3>
                <p className="text-gray-600">
                  Toutes nos réparations sont garanties 3 mois. Pièces Premium testées avant et pendant la réparation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Questions fréquentes - Rive-de-Gier
          </h2>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-gray-900">
                Vous déplacez-vous à Rive-de-Gier ?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                Oui ! Nous proposons un service de réparation à domicile pour Rive-de-Gier et les environs. Vous pouvez aussi venir directement à notre atelier de Saint-Chamond (10 km).
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-gray-900">
                Combien coûte la réparation d'un écran iPhone à Rive-de-Gier ?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                Le tarif varie selon le modèle et la qualité d'écran choisie (Premium, Hard OLED ou Soft OLED). Contactez-nous par téléphone ou WhatsApp pour un devis gratuit et immédiat.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-gray-900">
                Combien de temps dure la réparation ?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                La plupart des réparations (écran, batterie) sont réalisées en 30 minutes à 2 heures. Nous nous engageons sur une intervention le jour même dans la majorité des cas.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 px-4 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">
            iPhone cassé à Rive-de-Gier ? Contactez-nous !
          </h2>
          <p className="text-xl mb-8">
            Intervention rapide depuis Saint-Chamond - Devis gratuit par téléphone
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="tel:0773601521" className="text-2xl font-bold hover:underline">
              ☎️ 07.73.60.15.21
            </a>
            <Link to="/rendez-vous">
              <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold">
                Prendre rendez-vous
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <AvisVille slug="rive-de-gier" nom='Rive-de-Gier' />

      <VillesVoisines slug="rive-de-gier" />

      <footer className="bg-gray-900 text-white py-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">© 2026 Suro Phone - Réparation iPhone Rive-de-Gier</p>
          <p className="text-sm text-gray-500 mt-2">Atelier à Saint-Chamond - Intervention à Rive-de-Gier</p>
        </div>
      </footer>
    </div>
  );
};

export default ReparationIPhoneRiveDeGier;